import { MdSettings as Settings } from 'react-icons/md';
import { MdDeleteOutline as DeleteOutlineIcon } from 'react-icons/md';
import { MdSave as SaveOutlinedIcon } from 'react-icons/md';
import { LoadingButton } from '@mui/lab';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useSnapshot } from 'valtio';
import CustomTooltip from '../../common/components/CustomTooltip';
import { useIsUserReadOnlyRole } from '../../members/api/user-roles-api';
import { IConnectorButtonGroupProps } from '../connectors-types';
import ConnectorConfigureStore from '../stores/connector-configure-store';
import TokenVerifiedTextFieldAndorment from './TokenVerifiedTextFieldAndorment';
import ScanStore from '../../new-scan/store/scan-store';

const ConnectorButtonGroup: React.FC<
  React.PropsWithChildren<IConnectorButtonGroupProps>
> = ({
  disabledConnect,
  onVerifyAndAdd,
  onRemoveCredentials,
  isConnectorConfigured,
  validateConnector,
  connectorValidationInProgress,
  showValidationResult,
  connectorIsValid,
  openConnectorSettings,
  isScanning,
}) => {
  const {
    isLoadingVerifyAndSaveCredentials,
    isLoadingRemoveCredentials,
    isSourceControlFamilyConnector: isSelectedConnectorSourceControl,
    selectedConnector,
  } = useSnapshot(ConnectorConfigureStore);

  const isReadOnlyRole = useIsUserReadOnlyRole();
  const { isDemoEnabled } = useSnapshot(ScanStore);

  const deleteTooltipTitle = isReadOnlyRole
    ? 'Read only user cannot delete credentials'
    : isDemoEnabled
    ? 'Demo account cannot delete credentials'
    : 'Delete credentials';
  const connectTooltipTitle = isReadOnlyRole
    ? 'Read only user cannot add credentials'
    : isDemoEnabled
    ? 'Demo account cannot add credentials'
    : 'Connect';
  const settingsTooltipTitle = isReadOnlyRole
    ? 'Read only user cannot edit connector settings'
    : isDemoEnabled
    ? 'Demo account cannot add credentials'
    : isScanning
    ? 'Cannot change connector settings during a scan'
    : 'Connector settings';

  return (
    <Box display='flex' justifyContent='space-between'>
      <span style={{ width: '100%' }}>
        <LoadingButton
          size='medium'
          variant='outlined'
          color='primary'
          onClick={validateConnector}
          loading={connectorValidationInProgress}
          disabled={!isConnectorConfigured || isDemoEnabled}
          style={{ height: '100%' }}
          endIcon={
            showValidationResult ? (
              <TokenVerifiedTextFieldAndorment
                isConnectorConfigured={isConnectorConfigured}
                isConnectorValid={connectorIsValid}
              />
            ) : undefined
          }>
          <Typography variant='subtitle2'>Verify Connectivity</Typography>
        </LoadingButton>
      </span>
      <Stack
        gap={2}
        width='100%'
        justifyContent='flex-end'
        direction='row'
        alignItems='center'>
        <CustomTooltip title={connectTooltipTitle}>
          <span>
            <LoadingButton
              size='medium'
              variant='outlined'
              color='success'
              onClick={onVerifyAndAdd}
              loading={isLoadingVerifyAndSaveCredentials}
              loadingPosition='center'
              disabled={
                isConnectorConfigured ||
                disabledConnect ||
                isReadOnlyRole ||
                isDemoEnabled
              }
              startIcon={<SaveOutlinedIcon fontSize='small' />}>
              <Typography>Connect</Typography>
            </LoadingButton>
          </span>
        </CustomTooltip>

        <CustomTooltip title={deleteTooltipTitle}>
          <span>
            <LoadingButton
              loading={isLoadingRemoveCredentials}
              loadingPosition='center'
              size='medium'
              variant='outlined'
              color='error'
              onClick={onRemoveCredentials}
              disabled={
                !isConnectorConfigured || isReadOnlyRole || isDemoEnabled
              }
              startIcon={<DeleteOutlineIcon fontSize='small' />}>
              <Typography>Delete</Typography>
            </LoadingButton>
          </span>
        </CustomTooltip>
        {isSelectedConnectorSourceControl && (
          <CustomTooltip title={settingsTooltipTitle}>
            <span>
              <IconButton
                size='medium'
                onClick={openConnectorSettings}
                disabled={
                  !isConnectorConfigured ||
                  isReadOnlyRole ||
                  !!selectedConnector?.credentials[0].tokenExpirationDate ||
                  selectedConnector?.isEmptyOfRepos ||
                  isScanning ||
                  isDemoEnabled
                }>
                <Settings />
              </IconButton>
            </span>
          </CustomTooltip>
        )}
      </Stack>
    </Box>
  );
};

export default ConnectorButtonGroup;
